import {
  createSlice,
  createAsyncThunk,
  createDraftSafeSelector,
} from '@reduxjs/toolkit';
import { api } from '../../api/api';

// The system-wide IANA zone every sequence cron is evaluated in. UTC until told otherwise,
// which is also what the scheduler falls back to.
export const DEFAULT_SCHEDULE_TIMEZONE = 'UTC';

// Normalise an axios error into something serialisable for rejectWithValue, so callers can
// branch on the status (409 stale timezone, 400 invalid cron, 422 unknown zone).
const errorPayload = (e) => ({
  status: e?.response?.status ?? null,
  detail: e?.response?.data?.detail ?? null,
  message: e?.message ?? 'Request failed',
});

export const loadScheduleTimezone = createAsyncThunk(
  'scheduleTimezone/load',
  async () => {
    const res = await api.settings.scheduleTimezone.get();
    return res?.timezone || DEFAULT_SCHEDULE_TIMEZONE;
  },
  {
    // Loaded once per session; skip if a load is already in flight.
    condition: (_, { getState }) => !getState().scheduleTimezone?.loading,
  },
);

export const previewScheduleTimezone = createAsyncThunk(
  'scheduleTimezone/preview',
  async (timezone, { rejectWithValue }) => {
    try {
      return await api.admin.scheduleTimezone.preview(timezone);
    } catch (e) {
      return rejectWithValue(errorPayload(e));
    }
  },
);

export const applyScheduleTimezone = createAsyncThunk(
  'scheduleTimezone/apply',
  async (body, { rejectWithValue }) => {
    try {
      return await api.admin.scheduleTimezone.apply(body);
    } catch (e) {
      return rejectWithValue(errorPayload(e));
    }
  },
);

const scheduleTimezoneSlice = createSlice({
  name: 'scheduleTimezone',
  initialState: {
    timezone: DEFAULT_SCHEDULE_TIMEZONE,
    loaded: false,
    loading: false,
    preview: null,
    previewLoading: false,
    applying: false,
  },
  reducers: {
    // For responses that already carry the zone (e.g. the admin dashboard config).
    setScheduleTimezone(state, action) {
      if (action.payload) {
        state.timezone = action.payload;
        state.loaded = true;
      }
    },
    clearScheduleTimezonePreview(state) {
      state.preview = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadScheduleTimezone.pending, (s) => {
        s.loading = true;
      })
      .addCase(loadScheduleTimezone.fulfilled, (s, a) => {
        s.loading = false;
        s.loaded = true;
        s.timezone = a.payload;
      })
      .addCase(loadScheduleTimezone.rejected, (s) => {
        // Keep the UTC default; the labels are informational, not load-bearing.
        s.loading = false;
      })
      .addCase(previewScheduleTimezone.pending, (s) => {
        s.previewLoading = true;
      })
      .addCase(previewScheduleTimezone.fulfilled, (s, a) => {
        s.previewLoading = false;
        s.preview = a.payload;
        // The preview reports the stored zone, so it doubles as a refresh.
        if (a.payload?.current_timezone) {
          s.timezone = a.payload.current_timezone;
        }
      })
      .addCase(previewScheduleTimezone.rejected, (s) => {
        s.previewLoading = false;
      })
      .addCase(applyScheduleTimezone.pending, (s) => {
        s.applying = true;
      })
      .addCase(applyScheduleTimezone.fulfilled, (s, a) => {
        s.applying = false;
        s.preview = null;
        s.timezone =
          a.payload?.schedule_timezone || a.meta.arg.timezone || s.timezone;
        s.loaded = true;
      })
      .addCase(applyScheduleTimezone.rejected, (s) => {
        s.applying = false;
      });
  },
});

export const { setScheduleTimezone, clearScheduleTimezonePreview } =
  scheduleTimezoneSlice.actions;

const selectSelf = (state) => state.scheduleTimezone;
export const selectScheduleTimezone = createDraftSafeSelector(
  selectSelf,
  (s) => s?.timezone || DEFAULT_SCHEDULE_TIMEZONE,
);
export const selectScheduleTimezoneLoaded = createDraftSafeSelector(
  selectSelf,
  (s) => !!s?.loaded,
);
export const selectScheduleTimezonePreview = createDraftSafeSelector(
  selectSelf,
  (s) => s?.preview || null,
);
export const selectScheduleTimezonePreviewLoading = createDraftSafeSelector(
  selectSelf,
  (s) => !!s?.previewLoading,
);
export const selectScheduleTimezoneApplying = createDraftSafeSelector(
  selectSelf,
  (s) => !!s?.applying,
);

export default scheduleTimezoneSlice.reducer;
